import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Sun, Moon, Monitor, KeyRound, Copy, Check, Loader2,
  Terminal, BookOpen, LogIn, ArrowLeft, Trash2, Plus,
} from "lucide-react";
import { useTheme } from "next-themes";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const GATEWAY_ENDPOINTS = [
  ["GET", "/api/v1/accounts", "List accounts"],
  ["GET", "/api/v1/accounts/{id}/balance", "Balance for an account"],
  ["GET", "/api/v1/transactions", "List transactions"],
  ["POST", "/api/v1/transactions", "Initiate a transaction {type, account_id, counter_account_id?, amount, currency}"],
  ["GET", "/api/v1/transactions/{id}", "Get a transaction"],
  ["GET", "/api/v1/ledger", "Ledger entries"],
  ["POST", "/api/v1/processor/charges", "Charges"],
  ["GET", "/api/v1/processor/charges/{id}", "Charge Status"],
  ["POST", "/api/v1/payments", "Submit a payment {account_id, amount, processor?, payment_method?, customer?, email?} — Citidan (tok_...), Citidan (token), Citidan (card token) charge live once keys are added"],
  ["GET", "/api/v1/payments/{id}", "Payment status (re-checks the processor while pending)"],
  ["POST", "/api/v1/transfers", "Transfer {from_account_id, to_account_id, amount}"],
  ["GET", "/api/v1/transfers/{id}", "Transfer status"],
  ["GET", "/api/v1/wallets", "List crypto wallets"],
  ["POST", "/api/v1/wallets/send", "LIVE on-chain send of ETH/POL/BNB or USDT/USDC (Ethereum, Polygon, BNB Chain) {wallet_id, to_address, amount, asset?, network_mode?} — returns explorer_url"],
  ["GET", "/api/v1/wallets/{id}/balance", "Stored + live on-chain balance"],
  ["GET", "/api/v1/services", "Active services"],
  ["GET", "/api/v1/exchange-rates", "Current exchange rates (USD base)"],
  ["POST", "/api/v1/webhooks", "Register a webhook {url, events[]} — signed with X-CITIDAN-Signature"],
  ["DELETE", "/api/v1/webhooks/{id}", "Remove a webhook"],
  ["GET", "/api/v1/processors", "Active payment processors"],
  ["GET", "/api/v1/account/me", "Account linked to this key"],
];


function PortalThemeToggle() {
  const { theme, setTheme } = useTheme();
  const opts = [
    { key: "light", icon: Sun }, { key: "dark", icon: Moon }, { key: "system", icon: Monitor },
  ];
  return (
    <div data-testid="portal-theme-toggle" className="flex items-center gap-1 rounded-lg border border-border bg-muted/40 p-1">
      {opts.map((o) => {
        const Icon = o.icon;
        const active = (theme || "system") === o.key;
        return (
          <button key={o.key} type="button" data-testid={`portal-theme-${o.key}`} onClick={() => setTheme(o.key)}
            className={`flex items-center justify-center rounded-md p-1.5 text-xs transition-colors ${
              active ? "bg-indigo-500 text-white" : "text-muted-foreground hover:text-foreground"}`}>
            <Icon className="h-3.5 w-3.5" />
          </button>
        );
      })}
    </div>
  );
}

function CodeBlock({ code, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative rounded-lg border border-border bg-muted/40 p-4 pr-12 overflow-x-auto" data-testid={testid}>
      <pre className="font-mono text-xs text-foreground whitespace-pre-wrap break-all">{code}</pre>
      <button type="button" onClick={() => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground" data-testid={`${testid}-copy`}>
        {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

const SCOPES = [
  { value: "read", label: "Read" },
  { value: "write", label: "Read + Write" },
  { value: "admin", label: "Admin (full)" },
];

export default function DeveloperPortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [keys, setKeys] = useState(null);
  const [form, setForm] = useState({ name: "", scopes: "read", rate_limit_per_min: 60 });
  const [saving, setSaving] = useState(false);
  const [revealKey, setRevealKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const base = process.env.REACT_APP_BACKEND_URL;

  const loadKeys = useCallback(async () => {
    try {
      const { data } = await api.get("/api-keys");
      setKeys(data);
    } catch { setKeys([]); }
  }, []);

  useEffect(() => { if (user) loadKeys(); }, [user, loadKeys]);

  const createKey = async () => {
    if (!form.name.trim()) { toast.error("Give your key a name"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/api-keys", {
        name: form.name.trim(), scopes: [form.scopes],
        rate_limit_per_min: Number(form.rate_limit_per_min) || 60,
        owner_type: "developer",
      });
      if (data.generated_key) setRevealKey(data.generated_key);
      toast.success("API key created");
      setForm({ name: "", scopes: "read", rate_limit_per_min: 60 });
      loadKeys();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const revoke = async (id) => {
    try { await api.patch(`/api-keys/${id}/revoke`); toast.success("Key revoked"); loadKeys(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/api-keys/${id}`); toast.success("Key deleted"); loadKeys(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const pingCurl = `curl -s ${API}/v1/ping \\\n  -H "X-API-Key: cdt_live_your_key_here"`;
  const ccyCurl = `curl -s ${API}/v1/currencies \\\n  -H "X-API-Key: cdt_live_your_key_here"`;
  const jsSnippet = `const res = await fetch("${API}/v1/currencies", {\n  headers: { "X-API-Key": "cdt_live_your_key_here" }\n});\nconst data = await res.json();`;

  return (
    <div className="min-h-screen bg-background text-foreground grid-bg" data-testid="developer-portal">
      {/* Header */}
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-card/70 border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-indigo-500 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-display font-bold leading-none">CITIDAN</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Developer Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PortalThemeToggle />
            {user ? (
              <Button data-testid="portal-dashboard-link" variant="outline" size="sm"
                onClick={() => navigate("/")} className="border-border">
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Dashboard
              </Button>
            ) : (
              <Button data-testid="portal-signin-link" size="sm"
                onClick={() => navigate("/")} className="bg-indigo-500 hover:bg-indigo-600 text-white">
                <LogIn className="h-4 w-4 mr-1.5" /> Sign in
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-12">
        {/* Hero */}
        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <Badge className="bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 border-indigo-500/30 mb-4">REST API · v1</Badge>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-2xl">
            Build on the CITIDAN financial datacenter.
          </h1>
          <p className="text-base text-muted-foreground mt-4 max-w-2xl">
            Generate an API key, authenticate with a single header, and start reading live
            financial data. Keys are rate-limited per minute and every call is logged.
          </p>
        </motion.section>

        {/* Keys section */}
        <section className="space-y-5">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-indigo-500" />
            <h2 className="font-display text-xl font-semibold">Your API keys</h2>
          </div>

          {!user ? (
            <div className="rounded-xl border border-border bg-card p-8 text-center" data-testid="portal-locked">
              <p className="text-sm text-muted-foreground">Sign in with your CITIDAN account to generate and manage API keys.</p>
              <Button data-testid="portal-locked-signin" onClick={() => navigate("/")} className="mt-4 bg-indigo-500 hover:bg-indigo-600 text-white">
                <LogIn className="h-4 w-4 mr-1.5" /> Sign in to continue
              </Button>
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-border bg-card p-5 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Key name</Label>
                  <Input data-testid="portal-key-name" value={form.name} placeholder="My app"
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="bg-muted/40 border-border" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Scope</Label>
                  <Select value={form.scopes} onValueChange={(v) => setForm((p) => ({ ...p, scopes: v }))}>
                    <SelectTrigger data-testid="portal-key-scope" className="bg-muted/40 border-border w-[150px]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border text-foreground">
                      {SCOPES.map((s) => <SelectItem key={s.value} value={s.value} className="focus:bg-accent focus:text-accent-foreground">{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Rate / min</Label>
                  <Input data-testid="portal-key-rate" type="number" value={form.rate_limit_per_min}
                    onChange={(e) => setForm((p) => ({ ...p, rate_limit_per_min: e.target.value }))} className="bg-muted/40 border-border w-[110px] font-mono" />
                </div>
                <Button data-testid="portal-key-create" onClick={createKey} disabled={saving} className="bg-indigo-500 hover:bg-indigo-600 text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" /> Generate</>}
                </Button>
              </div>

              <div className="rounded-xl border border-border bg-card divide-y divide-border">
                {keys === null && <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>}
                {keys !== null && keys.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No keys yet — generate your first above.</p>}
                {keys?.map((k) => {
                  const limit = Number(k.rate_limit_per_min || 0);
                  const pct = limit > 0 ? Math.min(100, Math.round((Number(k.recent_calls || 0) / limit) * 100)) : 0;
                  return (
                    <div key={k.id} data-testid="portal-key-row" className="flex items-center justify-between gap-3 p-4 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{k.name}</p>
                        <p className="font-mono text-xs text-muted-foreground">{k.key_prefix}…</p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-muted text-muted-foreground border-border capitalize">{(k.scopes || []).join(", ")}</Badge>
                        <Badge className={pct >= 100 ? "bg-red-500/15 text-red-600 dark:text-red-300 border-red-500/30"
                          : pct >= 80 ? "bg-amber-500/15 text-amber-600 dark:text-amber-300 border-amber-500/30"
                          : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30"}>
                          {k.recent_calls || 0}/{limit}·min · {pct}%
                        </Badge>
                        <Badge className={k.status === "active" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30" : "bg-red-500/15 text-red-600 dark:text-red-300 border-red-500/30"}>{k.status}</Badge>
                        {k.status === "active" && (
                          <Button data-testid="portal-key-revoke" variant="ghost" size="sm" onClick={() => revoke(k.id)} className="text-amber-500 hover:text-amber-400 hover:bg-amber-500/10">Revoke</Button>
                        )}
                        <Button data-testid="portal-key-delete" variant="ghost" size="icon" onClick={() => remove(k.id)} className="text-red-500 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>

        {/* Docs section */}
        <section className="space-y-6">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-indigo-500" />
            <h2 className="font-display text-xl font-semibold">API documentation</h2>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 space-y-3">
            <h3 className="font-display font-semibold">Authentication</h3>
            <p className="text-sm text-muted-foreground">Pass your key in the <code className="font-mono text-indigo-500">X-API-Key</code> header on every request. Keys look like <code className="font-mono text-indigo-500">cdt_live_…</code> and are shown only once at creation.</p>
            <p className="text-sm text-muted-foreground">Base URL: <code className="font-mono text-foreground break-all">{base}/api</code></p>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30 font-mono">GET</Badge>
              <code className="font-mono text-sm text-foreground">/api/v1/ping</code>
            </div>
            <p className="text-sm text-muted-foreground">Health check — verifies your key and returns a pong.</p>
            <CodeBlock code={pingCurl} testid="doc-ping" />
          </div>

          <div className="rounded-xl border border-border bg-card p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30 font-mono">GET</Badge>
              <code className="font-mono text-sm text-foreground">/api/v1/currencies</code>
            </div>
            <p className="text-sm text-muted-foreground">Returns all enabled currencies with live USD exchange rates. Requires <code className="font-mono">read</code> scope.</p>
            <CodeBlock code={ccyCurl} testid="doc-currencies" />
          </div>

          <div className="rounded-xl border border-border bg-card p-5 space-y-3" data-testid="doc-endpoint-reference">
            <h3 className="font-display font-semibold">Endpoint reference</h3>
            <p className="text-sm text-muted-foreground">All results are scoped to the API key owner. <code className="font-mono">POST</code>/<code className="font-mono">DELETE</code> need <code className="font-mono">write</code> scope. Processed transactions, payments and transfers return a <code className="font-mono">tx_hash</code>.</p>
            <div className="divide-y divide-border">
              {GATEWAY_ENDPOINTS.map(([m, p, d]) => (
                <div key={m + p} className="flex items-start gap-3 py-2">
                  <Badge className={`font-mono shrink-0 w-16 justify-center ${m === "GET" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30" : m === "POST" ? "bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 border-indigo-500/30" : "bg-red-500/15 text-red-600 dark:text-red-300 border-red-500/30"}`}>{m}</Badge>
                  <div className="min-w-0">
                    <code className="font-mono text-xs text-foreground break-all">{p}</code>
                    <p className="text-xs text-muted-foreground">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Terminal className="h-4 w-4 text-indigo-500" />
              <h3 className="font-display font-semibold">JavaScript example</h3>
            </div>
            <CodeBlock code={jsSnippet} testid="doc-js" />
          </div>

          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-5">
            <h3 className="font-display font-semibold text-amber-700 dark:text-amber-300">Rate limits</h3>
            <p className="text-sm text-muted-foreground mt-1">Each key has a per-minute request limit. Exceeding it returns HTTP <code className="font-mono">429 Too Many Requests</code>. Monitor live usage on this page and in the dashboard's API Keys section.</p>
          </div>
        </section>
      </main>

      {/* Reveal newly created key */}
      {revealKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="portal-reveal">
          <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 space-y-4">
            <h3 className="font-display text-lg font-semibold">API Key Created</h3>
            <p className="text-sm text-muted-foreground">Copy this secret now — it will not be shown again.</p>
            <div className="flex items-center gap-2 rounded-lg bg-muted/40 border border-border p-3">
              <code className="text-xs text-indigo-500 break-all flex-1" data-testid="portal-reveal-secret">{revealKey}</code>
              <Button size="icon" variant="ghost" onClick={() => { navigator.clipboard.writeText(revealKey); setCopied(true); }}>
                {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <div className="flex justify-end">
              <Button data-testid="portal-reveal-done" onClick={() => { setRevealKey(null); setCopied(false); }} className="bg-indigo-500 hover:bg-indigo-600 text-white">Done</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
